import { Eye, Lock, MoreHorizontal, Pencil, Trash2, Users } from "lucide-react";
import { Badge, Card, DropdownMenu } from "@/components/common";
import type { DropdownMenuItem } from "@/components/common/DropdownMenu";
import type { PermissionGroup, Role } from "@/types/rbac";

interface RoleCardProps {
  role: Role;
  permissionGroups: PermissionGroup[];
  onEdit: (role: Role) => void;
  onDelete: (role: Role) => void;
  canUpdate?: boolean;
  canDelete?: boolean;
}

function getPermissionLabels(
  permissions: string[],
  groups: PermissionGroup[],
): string[] {
  const allPerms = groups.flatMap((g) => g.permissions);
  return permissions
    .map((id) => allPerms.find((p) => p.id === id)?.label)
    .filter((label): label is string => label !== undefined);
}

export function RoleCard({
  role,
  permissionGroups,
  onEdit,
  onDelete,
  canUpdate = true,
  canDelete = true,
}: RoleCardProps) {
  const permLabels = getPermissionLabels(role.permissions, permissionGroups);
  const visiblePerms = permLabels.slice(0, 4);
  const remaining = permLabels.length - visiblePerms.length;

  const menuItems: DropdownMenuItem[] = [];

  if (role.isSystem) {
    // System roles: always allow viewing
    menuItems.push({
      label: "Podgląd",
      icon: <Eye size={14} />,
      onClick: () => {
        onEdit(role);
      },
    });
  } else {
    if (canUpdate) {
      menuItems.push({
        label: "Edytuj",
        icon: <Pencil size={14} />,
        onClick: () => {
          onEdit(role);
        },
      });
    }
    if (canDelete) {
      menuItems.push({
        label: "Usuń",
        icon: <Trash2 size={14} />,
        onClick: () => {
          onDelete(role);
        },
        variant: "danger",
        disabled: role.usersCount > 0,
      });
    }
  }

  return (
    <Card>
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-main">{role.name}</h3>
          {role.isSystem && (
            <Badge variant="info">
              <Lock size={10} aria-hidden="true" />
              Systemowa
            </Badge>
          )}
        </div>
        <DropdownMenu
          trigger={<MoreHorizontal size={16} aria-hidden="true" />}
          items={menuItems}
        />
      </div>

      {/* Description */}
      <p className="mt-1 text-sm text-dim">{role.description}</p>

      {/* Stats */}
      <div className="mt-3 flex gap-4 border-t border-edge-soft pt-3">
        <div className="flex items-center gap-1.5 text-xs text-dim">
          <Users size={14} aria-hidden="true" />
          <span>
            {role.usersCount}{" "}
            {role.usersCount === 1 ? "użytkownik" : "użytkowników"}
          </span>
        </div>
        <div className="text-xs text-dim">
          {role.permissions.length} uprawnień
        </div>
      </div>

      {/* Permission preview */}
      {visiblePerms.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {visiblePerms.map((label) => (
            <span
              key={label}
              className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-sub"
            >
              {label}
            </span>
          ))}
          {remaining > 0 && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-dim">
              +{remaining} więcej
            </span>
          )}
        </div>
      )}
    </Card>
  );
}
